import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FilePdf, MagnifyingGlass, FileText, Sparkle, CheckCircle, ClockClockwise, X, User, DownloadSimple, Trash, PencilSimple, FloppyDisk, Plus, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [munis, setMunis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ search: "", municipio_id: "", skip: 0, limit: 20 });

  const loadHistory = () => {
    api.get("/reports/history?limit=100").then((r) => setHistory(r.data.items));
  };
  const openHistory = () => { loadHistory(); setShowHistory(true); };

  useEffect(() => { api.get("/municipalities").then((r) => setMunis(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));
    api.get("/contracts", { params })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [filters]);

  const generateReport = async (c) => {
    setGenerating(c.id);
    try {
      const res = await fetch(`${API}/contracts/${c.id}/report`, { credentials: "include" });
      if (!res.ok) throw new Error("erro");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `relatorio_TCE-CE_${(c.numero || "contrato").replace(/\//g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Relatório do contrato ${c.numero} gerado`);
    } catch {
      toast.error("Erro ao gerar relatório");
    } finally {
      setGenerating(null);
    }
  };

  const downloadFromHistory = async (h) => {
    setGenerating(h.contract_id);
    try {
      const res = await fetch(`${API}/reports/history/${h.report_id}/download`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = h.filename || `relatorio_${h.contract_numero}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Arquivo anexado baixado");
    } catch {
      toast.error("Erro ao baixar arquivo arquivado");
    } finally { setGenerating(null); }
  };

  const generateWithIA = async (c) => {
    setGenerating(c.id + "-ia");
    try {
      toast.message("Analisando contrato com IA…", { description: "Isso pode levar até 30s" });
      await api.post(`/contracts/${c.id}/analyze`);
      toast.success("Análise IA concluída — gerando relatório PDF…");
      await generateReport(c);
    } catch (e) {
      toast.error("Erro na análise IA. Gerando relatório sem IA…");
      await generateReport(c);
    } finally {
      setGenerating(null);
    }
  };

  const renderList = () => {
    if (loading) return <div className="text-center text-[#94A3B8] py-10">Carregando…</div>;
    if (items.length === 0) return <div className="text-center text-[#94A3B8] py-10">Nenhum contrato encontrado.</div>;
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-label">Documentos</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Relatórios Contratuais</h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
            Gere relatórios PDF individualizados de cada contrato, com identificação completa, objeto, vigência,
            valor e — opcionalmente — análise técnica por inteligência artificial (Claude Sonnet 4.5).
          </p>
        </div>
        <button
          data-testid="open-history-button"
          onClick={openHistory}
          className="px-4 py-2.5 rounded-md border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 text-sm flex items-center gap-2 transition-colors"
        >
          <ClockClockwise size={16} weight="duotone" /> Histórico de relatórios
        </button>
      </div>

      {/* Consolidado Mensal por Fiscal */}
      <ConsolidadoMensalCard isAdmin={isAdmin} munis={munis} userMuniName={user?.municipio_nome} />

      {/* Filtros */}
      <div className="tce-card p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              data-testid="report-search-input"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, skip: 0 })}
              placeholder="Buscar por número, objeto ou fornecedor…"
              className="tce-input w-full pl-10 pr-4 py-2.5 rounded-md text-sm"
            />
          </div>
          <select
            data-testid="report-filter-municipio"
            value={filters.municipio_id}
            onChange={(e) => setFilters({ ...filters, municipio_id: e.target.value, skip: 0 })}
            disabled={!isAdmin}
            className="tce-input px-3 py-2.5 rounded-md text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">{isAdmin ? "Todos os municípios" : (user?.municipio_nome || "Seu município")}</option>
            {munis.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
          </select>
        </div>
        <p className="text-xs text-[#94A3B8] mt-3">{total.toLocaleString("pt-BR")} contratos disponíveis</p>
      </div>

      {/* Lista */}
      <div className="space-y-3">
        {renderList()}
        {!loading && items.length > 0 && items.map((c, i) => (
            <div key={c.id} data-testid={`report-row-${i}`}
                 className="tce-card p-5 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-[#F59E0B] text-sm">{c.numero}</span>
                  <span className="text-xs text-[#94A3B8]">·</span>
                  <span className="text-xs text-[#94A3B8]">{c.municipio_nome}</span>
                  {c.ia_analise && (
                    <span className="text-xs text-[#10B981] flex items-center gap-1">
                      <CheckCircle size={12} weight="fill" /> Com análise IA
                    </span>
                  )}
                </div>
                <div className="text-sm text-[#E9EEF5] line-clamp-2">{c.objeto || "—"}</div>
                <div className="flex items-center gap-4 mt-2 text-xs text-[#94A3B8]">
                  <span>{c.modalidade}</span>
                  <span className="font-mono text-[#FCD34D]">{fmtBRL(c.valor)}</span>
                  <span>{(c.data_inicio || "").slice(0, 10)} → {(c.data_fim || "").slice(0, 10)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  data-testid={`gen-report-${i}`}
                  onClick={() => generateReport(c)}
                  disabled={generating === c.id}
                  className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#F59E0B] text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <FilePdf size={16} weight="duotone" />
                  {generating === c.id ? "Gerando…" : "PDF"}
                </button>
                <button
                  data-testid={`gen-report-ia-${i}`}
                  onClick={() => generateWithIA(c)}
                  disabled={generating === c.id + "-ia"}
                  className="px-4 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <Sparkle size={16} weight="fill" />
                  {generating === c.id + "-ia" ? "Analisando…" : "PDF + IA"}
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* Paginação */}
      <div className="flex items-center justify-between pt-4 text-sm text-[#94A3B8]">
        <span>Página {Math.floor(filters.skip / filters.limit) + 1} de {Math.max(1, Math.ceil(total / filters.limit))}</span>
        <div className="flex gap-2">
          <button data-testid="report-page-prev" disabled={filters.skip === 0}
                  onClick={() => setFilters({ ...filters, skip: Math.max(0, filters.skip - filters.limit) })}
                  className="px-3 py-1.5 rounded border border-[#334155] hover:border-[#F59E0B] disabled:opacity-30">
            Anterior
          </button>
          <button data-testid="report-page-next" disabled={filters.skip + filters.limit >= total}
                  onClick={() => setFilters({ ...filters, skip: filters.skip + filters.limit })}
                  className="px-3 py-1.5 rounded border border-[#334155] hover:border-[#F59E0B] disabled:opacity-30">
            Próxima
          </button>
        </div>
      </div>

      {/* Modal de Histórico */}
      {showHistory && (
        <div
          data-testid="history-modal"
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowHistory(false)}
        >
          <div
            className="tce-card w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
              <div className="flex items-center gap-3">
                <ClockClockwise size={22} weight="duotone" className="text-[#F59E0B]" />
                <div>
                  <div className="uppercase-label">Auditoria de geração</div>
                  <div className="font-heading text-xl mt-1">Histórico de Relatórios Emitidos</div>
                </div>
              </div>
              <button
                data-testid="close-history-button"
                onClick={() => setShowHistory(false)}
                className="text-[#94A3B8] hover:text-[#F59E0B] p-2"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {history.length === 0 ? (
                <div className="px-6 py-16 text-center text-[#94A3B8]">
                  Nenhum relatório gerado ainda. Use os botões PDF / PDF + IA para emitir o primeiro.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#1E293B] sticky top-0">
                    <tr className="text-left">
                      <th className="px-5 py-3 uppercase-label">Contrato</th>
                      <th className="px-5 py-3 uppercase-label">Município</th>
                      <th className="px-5 py-3 uppercase-label">Fiscal Responsável</th>
                      <th className="px-5 py-3 uppercase-label">Emissão</th>
                      <th className="px-5 py-3 uppercase-label">IA</th>
                      <th className="px-5 py-3 uppercase-label text-right">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={`${h.contract_id}-${h.created_at}`}
                          data-testid={`history-row-${i}`}
                          className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                        <td className="px-5 py-3 font-mono text-[#F59E0B]">{h.contract_numero || "—"}</td>
                        <td className="px-5 py-3 text-[#94A3B8]">{h.contract_municipio}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#1E293B] flex items-center justify-center text-[#F59E0B]">
                              <User size={14} weight="duotone" />
                            </div>
                            <div>
                              <div className="text-[#E9EEF5]">{h.fiscal_name}</div>
                              <div className="text-xs text-[#94A3B8]">{h.fiscal_email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-xs text-[#94A3B8]">
                          {new Date(h.created_at).toLocaleString("pt-BR")}
                        </td>
                        <td className="px-5 py-3">
                          {h.with_ia ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] flex items-center gap-1 w-fit">
                              <Sparkle size={10} weight="fill" /> Com IA
                            </span>
                          ) : (
                            <span className="text-xs text-[#94A3B8]">Padrão</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              data-testid={`history-edit-${i}`}
                              onClick={() => setEditing(h)}
                              title="Editar conteúdo do relatório"
                              className="px-3 py-1.5 rounded-md border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 hover:border-[#F59E0B] text-xs font-medium flex items-center gap-1.5 transition-colors"
                            >
                              <PencilSimple size={14} weight="bold" /> Editar
                            </button>
                            <button
                              data-testid={`history-download-${i}`}
                              onClick={() => downloadFromHistory(h)}
                              disabled={generating === h.contract_id}
                              className="px-3 py-1.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                            >
                              <DownloadSimple size={14} weight="bold" />
                              {generating === h.contract_id ? "Baixando…" : "Baixar PDF"}
                            </button>
                            <button
                              data-testid={`history-delete-${i}`}
                              onClick={async () => {
                                if (!window.confirm(`Excluir do histórico o relatório do contrato ${h.contract_numero}? O PDF arquivado também será removido.`)) return;
                                try {
                                  await api.delete(`/reports/history/${h.report_id}`);
                                  toast.success("Relatório excluído");
                                  setHistory((prev) => prev.filter((x) => x.report_id !== h.report_id));
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Erro ao excluir");
                                }
                              }}
                              title="Excluir do histórico"
                              className="p-1.5 rounded text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                            >
                              <Trash size={14} weight="duotone" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="px-6 py-3 border-t border-[#334155] text-xs text-[#94A3B8] flex items-center justify-between">
              <span>{history.length} registro(s) exibido(s)</span>
              <button onClick={loadHistory} className="text-[#F59E0B] hover:text-[#FCD34D]">Atualizar</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <EditReportModal
          report={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadHistory(); }}
        />
      )}
    </div>
  );
}

function EditReportModal({ report, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [sections, setSections] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/reports/history/${report.report_id}/content`)
      .then((r) => {
        if (cancelled) return;
        setTitle(r.data.title || "Relatório de Acompanhamento Contratual");
        // Stable unique keys p/ cada seção (evita bugs de reordenação/remoção no React)
        setSections((r.data.sections || []).map((s, i) => ({ ...s, _key: `s${Date.now()}-${i}` })));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || "Erro ao carregar conteúdo do relatório");
        setLoading(false);
      });
    return () => { cancelled = true; };
    // onClose intencionalmente fora das deps: é recriada a cada render do pai e causaria refetch infinito
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.report_id]);

  const updateSection = (idx, field, value) => {
    setSections((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const addSection = () => setSections((prev) => [...prev, { heading: "Nova seção", content: "", _key: `s${Date.now()}-${prev.length}` }]);
  const removeSection = (idx) => setSections((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/reports/history/${report.report_id}/content`, { title, sections });
      toast.success("Relatório atualizado — PDF regenerado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="edit-report-modal">
      <div className="tce-card w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#334155]">
          <div>
            <div className="uppercase-label">Editar relatório</div>
            <div className="font-heading text-xl mt-1">Contrato {report.contract_numero}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md text-[#94A3B8] hover:text-[#E9EEF5]"><X size={18} /></button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-[#94A3B8]">Carregando…</div>
        ) : (
          <>
            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              <div>
                <label className="uppercase-label block mb-2">Título do relatório</label>
                <input data-testid="edit-title" value={title} onChange={(e) => setTitle(e.target.value)}
                       className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
              </div>
              {sections.map((s, i) => (
                <div key={s._key} data-testid={`edit-section-${i}`} className="border border-[#334155] rounded-md p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="uppercase-label">Seção {i + 1}</label>
                    <button onClick={() => removeSection(i)} title="Remover seção"
                            className="p-1.5 rounded text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10">
                      <Trash size={12} weight="duotone" />
                    </button>
                  </div>
                  <input value={s.heading} onChange={(e) => updateSection(i, "heading", e.target.value)}
                         placeholder="Título da seção"
                         className="tce-input w-full px-3 py-2 rounded-md text-sm mb-2 font-semibold" />
                  <textarea value={s.content} onChange={(e) => updateSection(i, "content", e.target.value)}
                            rows={6} placeholder="Conteúdo da seção (suporta **negrito**, listas com • ou -, e quebras de linha)"
                            className="tce-input w-full px-3 py-2 rounded-md text-sm font-mono leading-relaxed" />
                </div>
              ))}
              <button data-testid="edit-add-section" onClick={addSection}
                      className="w-full px-4 py-3 rounded-md border border-dashed border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-sm flex items-center justify-center gap-2 transition-colors">
                <Plus size={14} weight="bold" /> Adicionar nova seção
              </button>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#334155]">
              <button onClick={onClose} className="px-4 py-2.5 text-sm text-[#94A3B8] hover:text-[#E9EEF5]">Cancelar</button>
              <button data-testid="edit-save" onClick={save} disabled={saving}
                      className="px-5 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2 disabled:opacity-50">
                <FloppyDisk size={14} weight="bold" />
                {saving ? "Salvando e regerando PDF…" : "Salvar e regerar PDF"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function ConsolidadoMensalCard({ isAdmin, munis, userMuniName }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [muniId, setMuniId] = useState("");
  const [fiscalEmail, setFiscalEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);

  const buildParams = () => {
    const p = new URLSearchParams({ year: String(year), month: String(month) });
    if (muniId) p.set("municipio_id", muniId);
    if (fiscalEmail) p.set("fiscal_email", fiscalEmail);
    return p;
  };

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/consolidado-mensal-fiscal?${buildParams()}`);
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar consolidado");
    } finally {
      setLoading(false);
    }
  };

  const baixarPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/reports/consolidado-mensal-fiscal/pdf?${buildParams()}`,
        { credentials: "include" });
      if (!res.ok) throw new Error("erro");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consolidado_${year}_${String(month).padStart(2, "0")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Relatório consolidado gerado");
    } catch {
      toast.error("Erro ao gerar PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="consolidado-mensal-card" className="tce-card p-6 border-[#F59E0B]/30">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B]">
            <Calendar size={20} weight="duotone" />
          </div>
          <div>
            <div className="uppercase-label !text-[#F59E0B]">Prestação de contas mensal</div>
            <div className="font-heading text-xl mt-1">Consolidado por Fiscal</div>
            <p className="text-xs text-[#94A3B8] mt-1 max-w-2xl">
              Reúne todas as NFs/execuções do mês agrupadas por fiscal responsável e contrato.
              Ideal para apresentação ao TCE-CE no encerramento de competência.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <select
          data-testid="cons-month"
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          className="tce-input px-3 py-2.5 rounded-md text-sm"
        >
          {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select
          data-testid="cons-year"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="tce-input px-3 py-2.5 rounded-md text-sm"
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          data-testid="cons-muni"
          value={muniId}
          onChange={(e) => setMuniId(e.target.value)}
          disabled={!isAdmin}
          className="tce-input px-3 py-2.5 rounded-md text-sm disabled:opacity-60"
        >
          <option value="">{isAdmin ? "Todos os municípios" : (userMuniName || "Seu município")}</option>
          {munis.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
        </select>
        <input
          data-testid="cons-fiscal"
          value={fiscalEmail}
          onChange={(e) => setFiscalEmail(e.target.value)}
          placeholder="Email do fiscal (opcional)"
          className="tce-input px-3 py-2.5 rounded-md text-sm"
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button
          data-testid="cons-load"
          onClick={carregar}
          disabled={loading}
          className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#F59E0B] text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          <MagnifyingGlass size={16} /> {loading ? "Carregando…" : "Pré-visualizar"}
        </button>
        <button
          data-testid="cons-download"
          onClick={baixarPdf}
          disabled={downloading}
          className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2 disabled:opacity-50"
        >
          <FilePdf size={16} weight="duotone" />
          {downloading ? "Gerando…" : "Baixar PDF Consolidado"}
        </button>
      </div>

      {preview && (
        <div data-testid="cons-preview" className="border-t border-[#334155] pt-4">
          <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
            <div className="tce-input rounded-md px-4 py-3">
              <div className="uppercase-label">Fiscais ativos</div>
              <div className="font-heading text-2xl mt-1">{preview.qtd_fiscais || 0}</div>
            </div>
            <div className="tce-input rounded-md px-4 py-3">
              <div className="uppercase-label">NFs / Execuções</div>
              <div className="font-heading text-2xl mt-1">{preview.total_execucoes || 0}</div>
            </div>
            <div className="tce-input rounded-md px-4 py-3">
              <div className="uppercase-label">Valor Executado</div>
              <div className="font-heading text-2xl mt-1 gold-text">{fmtBRL(preview.total_valor)}</div>
            </div>
          </div>
          {preview.fiscais?.length > 0 ? (
            <div className="space-y-3">
              {preview.fiscais.map((f) => (
                <div key={f.fiscal_email} data-testid={`cons-fiscal-${f.fiscal_email}`} className="rounded-md border border-[#334155] p-3">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      <User size={14} className="text-[#F59E0B]" />
                      <span className="text-[#E9EEF5] font-medium">{f.fiscal_name}</span>
                      <span className="text-xs text-[#94A3B8]">{f.fiscal_email}</span>
                    </div>
                    <div className="text-xs text-[#94A3B8]">
                      {f.qtd_execucoes} NF(s) · {f.contratos?.length || 0} contrato(s) ·
                      <span className="text-[#FCD34D] font-mono ml-1">{fmtBRL(f.total_valor)}</span>
                    </div>
                  </div>
                  <div className="space-y-1 pl-6">
                    {f.contratos.slice(0, 5).map((c) => (
                      <div key={c.contract_id} className="text-xs text-[#94A3B8] flex items-center justify-between">
                        <span className="truncate"><span className="text-[#F59E0B] font-mono">{c.numero}</span> · {c.municipio_nome} · {(c.objeto || "").slice(0, 50)}</span>
                        <span className="text-[#FCD34D] font-mono ml-3 whitespace-nowrap">{fmtBRL(c.total_valor)} ({c.qtd_execucoes})</span>
                      </div>
                    ))}
                    {f.contratos.length > 5 && (
                      <div className="text-[10px] text-[#94A3B8] italic">+ {f.contratos.length - 5} contrato(s) — disponíveis no PDF completo</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-[#94A3B8] py-4 text-center">
              Nenhuma execução registrada em {MESES[month - 1]}/{year}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

